import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { type Deadline, EVENT_TYPES, type EventSummary, type EventType, useEvents } from '../api/events';
import { useMembership } from '../auth/session';
import { FilterChips } from '../components/FilterChips';
import { ErrorView } from '../components/Guards';
import { dateLocale } from '../i18n';
import { daysUntil, formatDayMonth, formatDayShort, formatWeekday } from '../lib/eventDates';
import ui from '../styles/ui.module.css';
import styles from './EventsPage.module.css';

/** Director: upcoming events of every type, with line-up, replies, readiness and the next deadline (wireframe 08). */
export function EventsPage() {
  const { t } = useTranslation();
  const membership = useMembership();
  const events = useEvents(membership?.choirId);
  const [filter, setFilter] = useState<EventType | null>(null);

  if (!membership) return null;
  const list = events.data ?? [];
  const shown = filter ? list.filter((e) => e.type === filter) : list;

  return (
    <div className={`${ui.page} ${styles.page}`}>
      <div className={ui.pageHeader}>
        <div>
          <h1>{t('events.title')}</h1>
          <div className={ui.subtitle}>{t('events.subtitle', { count: list.length })}</div>
        </div>
        <Link to="/events/new" className={`${ui.button} ${ui.primary}`}>
          {t('events.newEvent')}
        </Link>
      </div>

      <FilterChips
        label={t('events.colType')}
        value={filter}
        onChange={setFilter}
        options={[
          { key: null, label: t('events.all'), count: list.length },
          ...EVENT_TYPES.map((type) => ({ key: type, label: t(`eventTypes.${type}`), count: list.filter((e) => e.type === type).length })),
        ]}
      />

      <section className={ui.card}>
        {events.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
        {events.isError && <ErrorView onRetry={() => void events.refetch()} />}
        {events.data && shown.length === 0 && (
          <div className={ui.empty}>
            <p>{t('events.empty')}</p>
            {list.length === 0 && <p>{t('events.emptyHint')}</p>}
          </div>
        )}
        {shown.length > 0 && (
          <table className={ui.table}>
            <thead>
              <tr>
                <th>{t('events.colDate')}</th>
                <th>{t('events.colEvent')}</th>
                <th>{t('events.colType')}</th>
                <th>{t('events.colLineup')}</th>
                <th>{t('events.colReplies')}</th>
                <th>{t('events.colReadiness')}</th>
                <th>{t('events.colDeadline')}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((event) => (
                <EventRow key={event.id} event={event} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function EventRow({ event }: { event: EventSummary }) {
  const { t } = useTranslation();
  const locale = dateLocale();
  const ready = event.attending > 0 ? event.readyAll / event.attending : 0;
  return (
    <tr data-testid="event-row">
      <td>
        <strong>{event.endsOn ? `${formatDayShort(event.startsOn, locale)} – ${formatDayShort(event.endsOn, locale)}` : formatDayShort(event.startsOn, locale)}</strong>
        <span className={ui.secondary}>{formatWeekday(event.startsOn, locale)}</span>
      </td>
      <td>
        <Link to={`/events/${event.id}`}>{event.name}</Link>
        {event.venue && <span className={ui.secondary}>{event.venue}</span>}
      </td>
      <td>
        <span className={`${ui.chip} ${ui.chipNeutral}`}>{t(`eventTypes.${event.type}`)}</span>
      </td>
      <td className={styles.nowrap}>{event.limitedLineup ? t('events.limitedCount', { count: event.lineupCount }) : t('events.wholeChoir')}</td>
      <td>
        {event.attending} / {event.lineupCount}
        {event.noReply > 0 && (
          <span className={ui.secondary}>
            {[t('events.noReplyCount', { count: event.noReply })]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
      </td>
      <td>
        {!event.readinessTracking ? (
          <span className={ui.muted}>{t('events.attendanceOnly')}</span>
        ) : event.songCount === 0 ? (
          <span className={ui.muted}>{t('events.noRepertoire')}</span>
        ) : event.attending === 0 ? (
          t('events.readinessNobody', { count: event.songCount })
        ) : (
          <>
            {t('events.readiness', { count: event.songCount, ready: event.readyAll, attending: event.attending })}
            <span className={styles.bar} aria-hidden="true">
              <span className={ready <= 0.5 ? `${styles.barFill} ${styles.barLow}` : styles.barFill} style={{ width: `${ready * 100}%` }} />
            </span>
          </>
        )}
        {event.readinessTracking && (
          <Link to={`/events/${event.id}/repertoire`} className={styles.small}>
            {t('events.editRepertoire')}
          </Link>
        )}
      </td>
      <td>{event.nextDeadline ? <DeadlineCell deadline={event.nextDeadline} /> : <span className={ui.muted}>–</span>}</td>
    </tr>
  );
}

function DeadlineCell({ deadline }: { deadline: Deadline }) {
  const { t } = useTranslation();
  const days = daysUntil(deadline.date);
  const when = days === 0 ? t('events.today') : days === 1 ? t('events.tomorrow') : formatDayMonth(deadline.date, dateLocale());
  return (
    <>
      <span className={ui.secondary}>{t(`events.deadline.${deadline.code}`, { song: deadline.songTitle ?? '' })}</span>
      <strong className={days <= 14 ? styles.soon : undefined}>
        {when}
        {days > 1 && days <= 14 && ` · ${t('events.days', { count: days })}`}
      </strong>
    </>
  );
}
